import { Css, FilterDefs, Filters, multiFilter, singleFilter, toggleFilter, useTestIds } from "@homebound/beam";
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react";
import type { NamedFragment } from "src/fragments/commonFragments.generated";
import { MaterialType, MaterialTypeDetail } from "src/generated/graphqlTypes";
import { useMaterialTypes } from "src/hooks/enums/useMaterialTypes";
import {
  MaterialCatalog_ItemFragment,
  MaterialCatalog_MaterialAttributeDimensionFragment,
  MaterialCatalog_MaterialBrandFragment,
  useMaterialCatalogMetadataQuery,
} from "src/routes/libraries/material-catalog/MaterialCatalog.generated";
import { queryResult } from "src/utils";
import { MaterialCatalogQueryParams } from "./useMaterialCatalog";

type MaterialCatalogFilterProps = {
  settings: MaterialCatalogQueryParams;
  setSettings: Dispatch<SetStateAction<MaterialCatalogQueryParams>>;
};

export function MaterialCatalogFilter({ settings, setSettings }: MaterialCatalogFilterProps) {
  const result = useMaterialCatalogMetadataQuery();
  return queryResult(result, ({ items, materialBrands, materialVariantTags }) => (
    <MaterialCatalogFilterView
      items={items}
      brands={materialBrands}
      tags={materialVariantTags}
      settings={settings}
      setSettings={setSettings}
    />
  ));
}

type FilterProps = {
  items: MaterialCatalog_ItemFragment[];
  brands: MaterialCatalog_MaterialBrandFragment[];
  tags: NamedFragment[];
  settings: MaterialCatalogQueryParams;
  setSettings: Dispatch<SetStateAction<MaterialCatalogQueryParams>>;
};

type LocalFilters = {
  brand?: string[];
  item?: string;
  type?: MaterialType[];
  tag?: string[];
  includeDeleted?: boolean;
  attributeIds?: string[];
};

function MaterialCatalogFilterView({ items, brands, tags, settings, setSettings }: FilterProps) {
  const testIds = useTestIds({});
  const [localFilters, setLocalFilters] = useState<LocalFilters>({});
  const types = useMaterialTypes();

  const filterDefs = useMemo(() => {
    const item = items.find((i) => i.id === localFilters.item);
    return createFilterDefs(brands, items, types, tags, item?.materialAttributeDimensions ?? []);
  }, [brands, items, types, tags, localFilters.item]);

  useEffect(() => {
    // Reset the current page to 1 if the filters change
    setSettings(mapToProviderFilters(localFilters, settings.perPage));
  }, [localFilters, setLocalFilters, setSettings, settings.perPage]);

  return (
    <div css={Css.mwPx(250).h100.bgGray100.p2.$} {...testIds}>
      <h1 css={Css.xl.pb2.$}>Filter</h1>
      <Filters filterDefs={filterDefs} filter={localFilters} onChange={setLocalFilters} vertical />
    </div>
  );
}

function createFilterDefs(
  brands: MaterialCatalog_MaterialBrandFragment[],
  items: MaterialCatalog_ItemFragment[],
  types: MaterialTypeDetail[],
  tags: NamedFragment[],
  dimensions: MaterialCatalog_MaterialAttributeDimensionFragment[],
): FilterDefs<LocalFilters> {
  const type = multiFilter({
    label: "Material Type",
    options: types,
    getOptionValue: (o) => o.code,
    getOptionLabel: (o) => o.name,
  });

  const item = singleFilter({
    label: "Item",
    options: items,
    getOptionValue: (o) => o.id,
    getOptionLabel: (o) => o.name,
  });

  const brand = multiFilter({
    label: "Brand",
    options: brands,
    getOptionValue: (o) => o.id,
    getOptionLabel: (o) => o.name,
  });

  const tag = multiFilter({ label: "Tag", options: tags, getOptionValue: (o) => o.id, getOptionLabel: (o) => o.name });

  const includeDeleted = toggleFilter({ label: "Show Archived" });

  const dimensionFilters = dimensions.keyBy(
    ({ name }) => name,
    ({ name, values }) =>
      multiFilter({
        label: name,
        options: values ?? [],
        getOptionValue: (o) => o.id,
        // NOTE: This field might need to be updated to handle range values
        //    SC-55230
        getOptionLabel: (o) => o?.textValue ?? "",
      }),
  );

  return { type, item, brand, tag, ...dimensionFilters, includeDeleted };
}

function mapToProviderFilters(localFilters: LocalFilters, perPage: number): MaterialCatalogQueryParams {
  // map `localFilters` to the ProductCatalogProvider `filters` state object
  const { item, type, brand, tag, attributeIds, includeDeleted, ...others } = localFilters;
  const dynamicFilters = Object.values(others).flatMap((v) => v) as string[];
  return {
    itemId: localFilters.item ? [localFilters.item] : undefined,
    type: localFilters.type,
    brandId: localFilters.brand,
    tags: localFilters.tag,
    ...(dynamicFilters.nonEmpty ? { attributeIds: dynamicFilters } : {}),
    includeDeleted,
    page: 1,
    perPage,
  };
}
